import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../shared/PageTitle';
import { useSettings } from '../../contexts/SettingsContext';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const AdminSupportTickets = () => {
  const { settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const getFilteredTickets = () => {
    if (filter === 'all') return tickets;
    return tickets.filter(ticket => ticket.status.toLowerCase() === filter.toLowerCase());
  };

  const filteredTickets = getFilteredTickets();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('support-tickets-section');
    }, 50);
  };

  // Previous page
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('support-tickets-section');
      }, 50);
    }
  };

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('support-tickets-section');
      }, 50);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/support/my-tickets', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success) {
        setTickets(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching support tickets:', error);
      // toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open': return 'bg-danger';
      case 'In Progress': return 'bg-warning text-dark';
      case 'Resolved': return 'bg-success';
      case 'Closed': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'High': return 'bg-danger';
      case 'Medium': return 'bg-warning text-dark';
      case 'Low': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  function formatDate(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  const TicketModal = () => {
    if (!selectedTicket) return null;

    return (
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
              <h5 className="modal-title">
                <i className="fas fa-ticket-alt me-2"></i>
                Support Ticket Details
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)}
                style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
              ></button>
            </div>
            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Ticket ID:</strong> TKT-{String(selectedTicket.id).padStart(6, '0')}
                </div>
                <div className="col-md-6">
                  <strong>Created:</strong> {formatDate(selectedTicket.created_at)}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Priority:</strong>
                  <span className={`badge ${getPriorityBadgeClass(selectedTicket.priority)} ms-2`}>
                    {selectedTicket.priority}
                  </span>
                </div>
                <div className="col-md-6">
                  <strong>Status:</strong>
                  <span className={`badge ${getStatusBadgeClass(selectedTicket.status)} ms-2`}>
                    {selectedTicket.status}
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <strong>Subject:</strong>
                <p className="mt-1">{selectedTicket.subject}</p>
              </div>

              <div className="mb-3">
                <strong>Description:</strong>
                <p className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.description}</p>
              </div>

              {selectedTicket.resolved_at && (
                <div className="mb-3">
                  <strong>Resolved:</strong> {formatDate(selectedTicket.resolved_at)}
                </div>
              )}

              {selectedTicket.notes && (
                <div className="mb-3">
                  <strong>Support Notes:</strong>
                  <div className="alert alert-info">
                    <p className="mt-1 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.notes}</p>
                  </div>
                </div>
              )}

              {selectedTicket.status !== 'Resolved' && selectedTicket.status !== 'Closed' && (
                <div className="alert alert-warning">
                  <i className="fas fa-info-circle me-2"></i>
                  This ticket is still being processed. You&apos;ll receive updates via email.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 activity-container">
      <PageTitle
        icon="fa-headset"
        title="My Support Tickets"
        className="mb-4"
      />

      {/* Header Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setFilter('all')}
                  >
                    All Tickets ({tickets.length})
                  </button>
                  <button
                    type="button"
                    className={`btn ${filter === 'open' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setFilter('open')}
                  >
                    Open ({tickets.filter(t => t.status === 'Open').length})
                  </button>
                  <button
                    type="button"
                    className={`btn ${filter === 'in progress' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setFilter('in progress')}
                  >
                    In Progress ({tickets.filter(t => t.status === 'In Progress').length})
                  </button>
                  <button
                    type="button"
                    className={`btn ${filter === 'resolved' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setFilter('resolved')}
                  >
                    Resolved ({tickets.filter(t => t.status === 'Resolved').length})
                  </button>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/support')}
                >
                  <i className="fas fa-plus me-2"></i>
                  Submit New Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="row" id="support-tickets-section">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                {filter === 'all' ? 'All Support Tickets' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Tickets`}
              </h6>
            </div>
            <div className="card-body">
              {filteredTickets.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                  <p className="text-muted mb-3">
                    {tickets.length === 0 ? 'No support tickets found' : 'No tickets match the current filter'}
                  </p>
                  {tickets.length === 0 && (
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate('/support')}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Submit Your First Ticket
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Ticket ID</th>
                          <th>Subject</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Last Updated</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map(ticket => (
                          <tr key={ticket.id}>
                            <td className="font-monospace">
                              TKT-{String(ticket.id).padStart(6, '0')}
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: '250px' }}>
                                {ticket.subject}
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${getPriorityBadgeClass(ticket.priority)}`}>
                                {ticket.priority}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                                {ticket.status}
                              </span>
                            </td>
                            <td>
                              <small>{formatDate(ticket.created_at)}</small>
                            </td>
                            <td>
                              <small>{formatDate(ticket.updated_at)}</small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  setSelectedTicket(ticket);
                                  setShowModal(true);
                                }}
                              >
                                <i className="fas fa-eye me-1"></i>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {tickets.length > itemsPerPage && (
                    <div className="card-footer d-flex justify-content-between align-items-center">
                      <div>
                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTickets.length)} of {filteredTickets.length} tickets
                      </div>

                      <nav aria-label="Visitor pagination">
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                              <i className="fas fa-chevron-left"></i>
                            </button>
                          </li>

                          {/* Simplified pagination - show only certain page numbers */}
                          {Array.from({ length: totalPages }).map((_, index) => {
                            const pageNumber = index + 1;

                            // Show first page, last page, current page, and pages around current page
                            if (
                              pageNumber === 1 ||
                              pageNumber === totalPages ||
                              (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                            ) {
                              return (
                                <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                  <button className="page-link" onClick={() => paginate(pageNumber)}>
                                    {pageNumber}
                                  </button>
                                </li>
                              );
                            }

                            // Show ellipsis
                            if (
                              pageNumber === 2 && currentPage > 3 ||
                              pageNumber === totalPages - 1 && currentPage < totalPages - 2
                            ) {
                              return (
                                <li key={pageNumber} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return null;
                          })}

                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                              <i className="fas fa-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && <TicketModal />}
    </div>
  );
};

export default AdminSupportTickets; 